import { Trans, useI18n } from '~/common/context';
import {
  Dropdown,
  Flex,
  Icon,
  IconButton,
  IconProps,
  Tooltip,
  useConfirmation,
} from '@wyz/ui';
import * as React from 'react';
import { Link } from 'react-router';
import { useListLocationState } from '~/common/hooks';

type Disableable = {
  disabledReason?: React.ReactNode;
};

type Showable = {
  visible?: boolean;
};

type ActionState = Disableable & Showable;

type NavigationAction = (
  | {
      type: 'BUTTON';
      handler: () => void;
    }
  | {
      type: 'LINK';
      to: string;
    }
) &
  ActionState;

type DeleteAction = {
  handler: () => void;
  doConfirm?: boolean;
} & ActionState;

type DuplicateAction = {
  handler: () => void;
} & ActionState;

type DataTableMenuAction = {
  id: string;
  icon: IconProps['icon'];
  label: React.ReactNode;
  handler: () => void;
} & ActionState;

type DataTableActionsProps = {
  rowIdentifier: string;
  detail?: NavigationAction;
  edit?: NavigationAction;
  duplicate?: DuplicateAction;
  remove?: DeleteAction;
  actions?: Array<DataTableMenuAction>;
};

const INLINE_ACTION_COUNT = 2;

const BARE_BUTTON_CLASS = 'p-0 border-0';

const isShown = <T extends Showable>(action: T | undefined): action is T =>
  action !== undefined && action.visible !== false;

function NavigationIcon({
  action,
  id,
  icon,
}: {
  action: NavigationAction;
  id: string;
  icon: IconProps['icon'];
}) {
  const state = useListLocationState();

  if (action.disabledReason || action.type === 'BUTTON') {
    return (
      <IconButton
        id={id}
        icon={icon}
        variant='link'
        size='xs'
        iconSize='xs'
        className={BARE_BUTTON_CLASS}
        disabled={Boolean(action.disabledReason)}
        tooltip={action.disabledReason}
        onClick={action.type === 'BUTTON' ? action.handler : undefined}
      />
    );
  }

  return (
    <Link
      className='text-neutral-grey-6'
      to={action.to}
      state={state}
      data-cy={id}
    >
      <Icon id={id} icon={icon} size='xs' />
    </Link>
  );
}

function MenuItem({ action }: { action: DataTableMenuAction }) {
  const item = (
    <Dropdown.Item
      id={action.id}
      as='button'
      type='button'
      onClick={action.handler}
      disabled={Boolean(action.disabledReason)}
    >
      <Icon icon={action.icon} size='xxs' className='me-2' />
      {action.label}
    </Dropdown.Item>
  );

  if (!action.disabledReason) {
    return item;
  }

  return (
    <Tooltip label={action.disabledReason} placement='left'>
      <span className='d-block'>{item}</span>
    </Tooltip>
  );
}

export default function DataTableActions({
  rowIdentifier,
  detail,
  edit,
  duplicate,
  remove,
  actions = [],
}: DataTableActionsProps) {
  const { t } = useI18n();
  const ask = useConfirmation();

  const otherActions: Array<DataTableMenuAction> = [
    ...(isShown(duplicate)
      ? [
          {
            id: `duplicate-${rowIdentifier}`,
            icon: 'duplicate' as const,
            label: <Trans i18nKey='app.common.button.duplicate' />,
            handler: duplicate.handler,
            disabledReason: duplicate.disabledReason,
          },
        ]
      : []),
    ...(isShown(remove)
      ? [
          {
            id: `delete-${rowIdentifier}`,
            icon: 'trash' as const,
            label: <Trans i18nKey='app.common.buttons.delete' />,
            handler: () =>
              remove.doConfirm
                ? ask({
                    title: t('app.list.delete_modal.title'),
                    description: t('app.common.form.message_confirmation'),
                    cancelBtnText: t('app.common.button.cancel'),
                    confirmBtnText: t('app.common.button.confirm'),
                    confirmBtnVariant: 'danger',
                  }).onConfirm(remove.handler)
                : remove.handler(),
            disabledReason: remove.disabledReason,
          },
        ]
      : []),
    ...actions.filter(isShown),
  ];
  const shownDetail = isShown(detail) ? detail : undefined;
  const shownEdit = isShown(edit) ? edit : undefined;
  // Two actions always stay on the row: the free slots left by view and edit go to the first other
  // actions, in order.
  const freeSlots =
    INLINE_ACTION_COUNT - [shownDetail, shownEdit].filter(Boolean).length;
  const inlineActions = otherActions.slice(0, freeSlots);
  const overflowActions = otherActions.slice(freeSlots);

  if (!shownDetail && !shownEdit && otherActions.length === 0) {
    return null;
  }

  return (
    <Flex className='actions d-flex align-items-center space-x-2 me-2' inline>
      {shownDetail && (
        <NavigationIcon
          action={shownDetail}
          id={`detail-${rowIdentifier}`}
          icon='eye'
        />
      )}
      {shownEdit && (
        <NavigationIcon
          action={shownEdit}
          id={`edit-${rowIdentifier}`}
          icon='pencil-line'
        />
      )}
      {inlineActions.map((action) => (
        <IconButton
          key={action.id}
          id={action.id}
          icon={action.icon}
          variant='link'
          size='xs'
          iconSize='xs'
          className={BARE_BUTTON_CLASS}
          disabled={Boolean(action.disabledReason)}
          tooltip={action.disabledReason ?? action.label}
          onClick={action.handler}
        />
      ))}
      {overflowActions.length > 0 && (
        <Dropdown.Container>
          <Dropdown.Toggle
            bsPrefix='toggle'
            as='button'
            type='button'
            className={`btn btn-link ${BARE_BUTTON_CLASS} text-neutral-grey-6`}
            id={`actions-${rowIdentifier}`}
            aria-label={t('app.common.actions')}
          >
            <Icon icon='dots' size='xs' />
          </Dropdown.Toggle>
          <Dropdown.Menu
            renderOnMount
            popperConfig={{ strategy: 'fixed' }}
            className='dropdown-caret dropdown-menu-card dropdown-menu-end'
          >
            {overflowActions.map((action) => (
              <MenuItem key={action.id} action={action} />
            ))}
          </Dropdown.Menu>
        </Dropdown.Container>
      )}
    </Flex>
  );
}
